import * as Sentry from "@sentry/nextjs";
import { ClientError, GraphQLClient, type RequestDocument } from "graphql-request";
import { ensureSessionFresh, isUnauthorizedHttpError, markSessionExpired } from "@/lib/auth/session-recovery";
import { isSentinelValue } from "@/lib/env";
import { setRequestIdTag } from "@/lib/sentry/issue-tags";
import { uiMetricCount, uiMetricDistribution } from "@/lib/sentry/metrics";

// Get the GraphQL endpoint URL - use absolute URL for graphql-request compatibility
// TODO: Added _ to pass lint checks. If this is not needed, remove it.
const _getGraphqlUrl = (): string => {
  // In the browser, use the current origin (works with Next.js rewrites)
  if (typeof window !== "undefined") {
    return `${window.location.origin}/api/graphql`;
  }
  // SSR fallback (should rarely be used since queries are client-side)
  return "/api/graphql";
};

const resolveGraphqlEndpoint = () => {
  if (typeof window !== "undefined") {
    return new URL("/api/graphql", window.location.origin).toString();
  }

  const graphqlEndpoint = process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT;
  if (graphqlEndpoint && !isSentinelValue(graphqlEndpoint)) {
    return graphqlEndpoint;
  }

  const apiBase = process.env.NEXT_PUBLIC_API_BASE_URL;
  if (apiBase && !isSentinelValue(apiBase)) {
    return new URL("/api/graphql", apiBase).toString();
  }

  return "http://localhost:8080/api/graphql";
};

const graphqlClient = new GraphQLClient(resolveGraphqlEndpoint(), {
  credentials: "include",
  fetch: (input, init) => {
    const headers = new Headers(init?.headers);
    const traceData = Sentry.getTraceData();
    if (traceData["sentry-trace"]) {
      headers.set("sentry-trace", traceData["sentry-trace"]);
    }
    if (traceData.baggage) {
      headers.set("baggage", traceData.baggage);
    }

    return fetch(input, { ...init, headers }).then((response) => {
      const requestId = response.headers.get("X-Request-ID");
      if (requestId) {
        setRequestIdTag(requestId);
        Sentry.getActiveSpan()?.setAttribute("http.response.request_id", requestId);
      }
      return response;
    });
  },
});

export const graphqlErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof ClientError) {
    const firstError = error.response?.errors?.[0];
    if (firstError) {
      // Prefer specific validation field messages over Lighthouse's generic wrapper.
      // e.g. extensions.validation = { org: ["No org found."] }
      const validation = (firstError as { extensions?: { validation?: Record<string, string[]> } }).extensions
        ?.validation;
      if (validation) {
        const messages = Object.values(validation).flat().filter(Boolean);
        if (messages.length > 0) return messages.join(" ");
      }
      if (firstError.message) return firstError.message;
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

export const graphqlRequest = async <TData, TVariables extends Record<string, unknown> = Record<string, never>>(
  document: RequestDocument,
  variables?: TVariables,
): Promise<TData> => {
  const startedAt = performance.now();
  uiMetricCount("ui.graphql.request", 1);

  const emptyVariables: Record<string, unknown> = {};
  const executeRequest = (vars: Record<string, unknown>) => {
    return graphqlClient.request<TData, Record<string, unknown>>(document, vars);
  };

  const recordOutcome = (outcome: "success" | "failed") => {
    uiMetricDistribution("ui.graphql.latency", performance.now() - startedAt, "millisecond", { outcome });
  };

  try {
    if (variables === undefined) {
      const result = await executeRequest(emptyVariables);
      recordOutcome("success");
      return result;
    }

    const result = await executeRequest(variables);
    recordOutcome("success");
    return result;
  } catch (error) {
    if (!isUnauthorizedHttpError(error)) {
      recordOutcome("failed");
      Sentry.metrics.count("ui.graphql.error", 1, {
        attributes: {
          status_family:
            error instanceof ClientError ? String(Math.trunc(error.response.status / 100) * 100) : "unknown",
        },
      });
      throw error;
    }

    // WorkOS session refresh is handled server-side in auth middleware.
    // Single-flight /api/auth/status nudges cookie rotation, then we retry once.
    const refreshed = await ensureSessionFresh();
    if (!refreshed) {
      recordOutcome("failed");
      uiMetricCount("ui.auth.failure", 1);
      throw error;
    }

    uiMetricCount("ui.auth.session_refresh", 1, { outcome: "success" });

    try {
      if (variables === undefined) {
        const result = await executeRequest(emptyVariables);
        recordOutcome("success");
        return result;
      }

      const result = await executeRequest(variables);
      recordOutcome("success");
      return result;
    } catch (retryError) {
      recordOutcome("failed");
      if (isUnauthorizedHttpError(retryError)) {
        markSessionExpired();
      }
      throw retryError;
    }
  }
};
